import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';

import { QUERY_KEYS } from 'consts/queryKeys';
import { getAccommodationsMainNavigation } from 'helper/navigation/accommodations/main';
import { useCustomToast } from 'hooks';
import { updatePassword, updateProfileDetails } from 'modules/settings/api';
import { SettingsNavigationState } from 'services/settings/navigation';

export const useMutationProfileDetails = () => {
  const { setUserClaims, setMainNavigationConfig } = useContext(SettingsNavigationState);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { successToast, errorToast } = useCustomToast();

  const {
    mutateAsync: updateCurrentProfileDetails,
    isLoading: isLoadingProfileDetails,
    isError: isErrorProfileDetails,
  } = useMutation(updateProfileDetails, {
    onSuccess: (_, { payload }) => {
      queryClient.refetchQueries(QUERY_KEYS.SETTINGS_PROFILE_DETAILS);
      queryClient.refetchQueries(QUERY_KEYS.SETTINGS_PROFILE);

      // update claims and navigation when user changes rentals settings
      const claims = payload.claims?.map((claim: any) => claim.name);

      setUserClaims(claims);
      setMainNavigationConfig(getAccommodationsMainNavigation(claims));

      successToast(t('Your profile details are successfully saved.'));
    },
    onError: (res: AxiosError) => {
      const errorData: any = res?.response?.data;
      const {
        items: { phone },
      } = errorData || {};
      errorToast(phone);
    },
  });

  const { mutateAsync: updateUserPassword, isLoading: isLoadingMutateUserPassword } = useMutation(
    updatePassword,
    {
      onSuccess: () => {
        successToast(t('Your password is successfully changed.'));
      },
      onError: () => errorToast(),
    },
  );

  return {
    updateCurrentProfileDetails,
    isLoadingProfileDetails,
    isErrorProfileDetails,
    updateUserPassword,
    isLoadingMutateUserPassword,
  };
};
